/**
 * StyleManager - Manages CSS styles and responsive breakpoints
 *
 * Mobile-first approach matching the current runtime:
 *   Base: mobile styles (no media query)
 *   @media (min-width: 768px): tablet
 *   @media (min-width: 1440px): desktop
 *
 * Only applies styles from an allowed whitelist.
 * Generates selectors scoped to data-dr-section containers.
 */

const ALLOWED_STYLE_PROPS = [
  // Text
  'textAlign', 'fontWeight', 'fontSize', 'letterSpacing',
  'lineHeight', 'color', 'textTransform', 'opacity',
  // Spacing
  'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
  'marginTop', 'marginBottom',
  // Layout
  'gap', 'justifyContent', 'alignItems', 'flexDirection',
  // Background
  'backgroundColor', 'borderRadius',
];

export class StyleManager {
  private styleSheets: Map<string, HTMLStyleElement> = new Map();

  /**
   * Handle a style-update message from the admin panel.
   * Each property value can be either a flat string or a responsive object
   * { mobile, tablet, desktop }.
   */
  handleStyleUpdate(sectionId: string, field: string, styles: Record<string, any>): void {
    const sectionEl = document.querySelector(`[data-dr-section="${sectionId}"]`);
    if (!sectionEl) return;

    const el = field === '__section'
      ? sectionEl as HTMLElement
      : sectionEl.querySelector(`[data-dr-style="${field}"]`) as HTMLElement | null;
    if (!el) return;

    for (const [prop, value] of Object.entries(styles)) {
      if (!ALLOWED_STYLE_PROPS.includes(prop)) continue;

      // Check if value is responsive (object with mobile key)
      if (typeof value === 'object' && value !== null && !Array.isArray(value) && value.mobile) {
        this.applyResponsiveStyle(sectionId, field, prop, value);
      } else {
        (el.style as any)[prop] = value;
      }
    }
  }

  /**
   * Apply a responsive style for a single property.
   * Creates/updates a <style> tag with mobile-first media queries.
   */
  private applyResponsiveStyle(
    sectionId: string,
    field: string,
    property: string,
    value: { mobile: string; tablet: string; desktop: string }
  ): void {
    // Apply mobile (base) style directly to the element
    const sectionEl = document.querySelector(`[data-dr-section="${sectionId}"]`);
    if (!sectionEl) return;

    const el = field === '__section'
      ? sectionEl as HTMLElement
      : sectionEl.querySelector(`[data-dr-style="${field}"]`) as HTMLElement | null;
    if (!el) return;

    (el.style as any)[property] = value.mobile;

    // Create/update style tag with media queries
    const styleId = `responsive-${sectionId}-${field}-${property}`;

    const selector = field === '__section'
      ? `[data-dr-section="${sectionId}"]`
      : `[data-dr-section="${sectionId}"] [data-dr-style="${field}"]`;

    const css = `
      ${selector} {
        ${this.camelToKebab(property)}: ${value.mobile} !important;
      }
      @media (min-width: 768px) {
        ${selector} {
          ${this.camelToKebab(property)}: ${value.tablet} !important;
        }
      }
      @media (min-width: 1440px) {
        ${selector} {
          ${this.camelToKebab(property)}: ${value.desktop} !important;
        }
      }
    `;

    this.injectStyleSheet(styleId, css);
  }

  /**
   * Inject CSS into document as a style element.
   */
  private injectStyleSheet(id: string, css: string): void {
    // Remove existing
    this.removeStyleSheet(id);

    const styleElement = document.createElement('style');
    styleElement.id = id;
    styleElement.textContent = css;
    document.head.appendChild(styleElement);
    this.styleSheets.set(id, styleElement);
  }

  /**
   * Remove a style element by ID.
   */
  private removeStyleSheet(id: string): void {
    const existing = this.styleSheets.get(id);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
      this.styleSheets.delete(id);
    }
    // Also check by DOM ID in case we missed the map entry
    const byId = document.getElementById(id) as HTMLStyleElement | null;
    if (byId && byId.parentNode) {
      byId.parentNode.removeChild(byId);
    }
  }

  private camelToKebab(str: string): string {
    return str.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
  }

  getActiveStyleSheets(): string[] {
    return Array.from(this.styleSheets.keys());
  }

  clearAllStyleSheets(): void {
    for (const id of this.styleSheets.keys()) {
      this.removeStyleSheet(id);
    }
  }

  destroy(): void {
    this.clearAllStyleSheets();
  }
}
